import { useMemo } from 'react';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { match, P } from 'ts-pattern';
import {
  type IxcRegistrationV1Employment,
  IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields,
  type IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest,
  type IxcRegistrationV1ProfileState,
  type IxcRegistrationV1ProfileSummaryData,
  type IxcRegistrationV1ProfileType,
} from '@interxfi/types/generated/api';
import { formatName } from '../shared-utils/name';

export function getPhysicalAddressFromProfileType(profile: IxcRegistrationV1ProfileType | undefined) {
  if (!profile) {
    return undefined;
  }

  if ('individual' in profile && profile.individual) {
    return profile.individual.physicalAddress;
  }

  if ('organization' in profile && profile.organization) {
    return profile.organization.companyAddress;
  }

  return undefined;
}

export function getPhysicalAddress(profile: IxcRegistrationV1ProfileState | undefined) {
  if (!profile?.data) {
    return undefined;
  }

  if ('currentVersion' in profile.data && profile.data.currentVersion?.profile) {
    return getPhysicalAddressFromProfileType(profile.data.currentVersion.profile);
  }

  if ('pendingVersion' in profile.data && profile.data.pendingVersion?.profile) {
    return getPhysicalAddressFromProfileType(profile.data.pendingVersion.profile);
  }

  return undefined;
}

export function getBirthDateFromProfileType(profile: IxcRegistrationV1ProfileType | undefined) {
  if (!profile) {
    return '';
  }

  if ('individual' in profile && profile.individual) {
    return profile.individual.birthDate;
  }

  return '';
}

export function getBirthDate(profile: IxcRegistrationV1ProfileState | undefined) {
  if (!profile?.data) {
    return undefined;
  }

  if ('currentVersion' in profile.data && profile.data.currentVersion?.profile) {
    return getBirthDateFromProfileType(profile.data.currentVersion.profile);
  }

  if ('pendingVersion' in profile.data && profile.data.pendingVersion?.profile) {
    return getBirthDateFromProfileType(profile.data.pendingVersion.profile);
  }

  return undefined;
}

export function getTaxIDFromProfileType(profile: IxcRegistrationV1ProfileType | undefined) {
  if (!profile) {
    return '';
  }

  if ('individual' in profile && profile.individual) {
    if ('ssn' in profile.individual.taxIdentifier && profile.individual.taxIdentifier.ssn) {
      return profile.individual.taxIdentifier.ssn.number;
    }

    if ('itin' in profile.individual.taxIdentifier && profile.individual.taxIdentifier.itin) {
      return profile.individual.taxIdentifier.itin.number;
    }

    return '';
  }

  if ('organization' in profile && profile.organization) {
    if ('ssn' in profile.organization.taxIdentifier && profile.organization.taxIdentifier.ssn) {
      return profile.organization.taxIdentifier.ssn.number;
    }

    if ('ein' in profile.organization.taxIdentifier && profile.organization.taxIdentifier.ein) {
      return profile.organization.taxIdentifier.ein.number;
    }

    return '';
  }
}

export function getTaxID(profile: IxcRegistrationV1ProfileState | undefined) {
  if (!profile?.data) {
    return '';
  }

  if ('currentVersion' in profile.data && profile.data.currentVersion?.profile) {
    return getTaxIDFromProfileType(profile.data.currentVersion.profile);
  }

  if ('pendingVersion' in profile.data && profile.data.pendingVersion?.profile) {
    return getTaxIDFromProfileType(profile.data.pendingVersion.profile);
  }

  return '';
}

export function getNameFromProfileSummaryType(profile: IxcRegistrationV1ProfileSummaryData | undefined) {
  if (!profile) {
    return '';
  }

  if ('individual' in profile && profile.individual) {
    return formatName(profile.individual.name);
  }

  if ('organization' in profile && profile.organization) {
    return profile.organization.legalName || '';
  }

  return '';
}

export function getNameFromProfileType(profile: IxcRegistrationV1ProfileType | undefined) {
  if (!profile) {
    return '';
  }

  if ('individual' in profile && profile.individual) {
    return formatName(profile.individual.name);
  }

  if ('organization' in profile && profile.organization) {
    return profile.organization.legalName || '';
  }

  return '';
}

export function getProfileName(profile: IxcRegistrationV1ProfileState | undefined) {
  if (!profile?.data) {
    return '';
  }

  if ('currentVersion' in profile.data && profile.data.currentVersion?.profile) {
    return getNameFromProfileType(profile.data.currentVersion.profile);
  }

  if ('pendingVersion' in profile.data && profile.data.pendingVersion?.profile) {
    return getNameFromProfileType(profile.data.pendingVersion.profile);
  }

  return '';
}

export function getProfileEmploymentInformation(data: IxcRegistrationV1Employment | undefined, key: 'occupation' | 'institution') {
  if (!data) {
    return undefined;
  }

  if ('employed' in data) {
    return data.employed[key];
  }

  if ('selfEmployed' in data) {
    return data.selfEmployed[key];
  }

  if ('partTime' in data) {
    return data.partTime[key];
  }

  if ('seasonal' in data) {
    return data.seasonal[key];
  }

  return undefined;
}

export function useEnsureProfileDataRequest(
  profileIds: string | string[] | undefined,
): IxcRegistrationV1ProfileQueryServiceProfileListProfileListRequest | undefined {
  return useMemo(() => {
    if (!profileIds?.length) {
      return undefined;
    }

    return {
      query: {
        filters: [
          {
            field: {
              name: IxcRegistrationV1ProfileQueryServiceProfileListFilterableFields.ProfileId,
              type: Array.isArray(profileIds)
                ? {
                    in: {
                      values: profileIds,
                    },
                  }
                : {
                    value: profileIds,
                  },
            },
          },
        ],
      },
    };
  }, [profileIds]);
}

export function getW9SignedDateForProfile(profile: IxcRegistrationV1ProfileState) {
  return match(profile)
    .with({ data: { currentVersion: { profile: { individual: P.not(P.nullish) } } } }, (p) => p.data.currentVersion.profile.individual.w9?.signed)
    .with({ data: { currentVersion: { profile: { organization: P.not(P.nullish) } } } }, (p) => p.data.currentVersion.profile.organization.w9.signed)
    .with({ data: { pendingVersion: { profile: { individual: P.not(P.nullish) } } } }, (p) => p.data.pendingVersion.profile.individual.w9?.signed)
    .with({ data: { pendingVersion: { profile: { organization: P.not(P.nullish) } } } }, (p) => p.data.pendingVersion.profile.organization.w9.signed)
    .otherwise(() => undefined);
}

// Primary account holder must have a signed W-9 on file that is less than one year old
export function checkIfProfileCanBePrimaryAccountHolder(profile: IxcRegistrationV1ProfileState): boolean {
  const signed = getW9SignedDateForProfile(profile);
  if (!signed) {
    return false;
  }

  return parseDate(signed).add({ years: 1 }).compare(today(getLocalTimeZone())) > 0;
}
